import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Config, driver, Driver, Popover } from 'driver.js';

import { LOT_BIDDING_MODAL_GUIDE_ANCHORS } from '../constants/lot-bidding-modal-guide-anchors.const';
import { LotBiddingModalDriveStep } from '../types/lot-bidding-modal-drive-step.type';
import { LotBiddingModalGuideStepName } from '../types/lot-bidding-modal-guide-step-name.type';
import {
  RemoveNewGroupGuideTarget,
  StartLotBiddingModalGuideParamsType,
} from '../types/start-lot-bidding-modal-guide-params.type';

@Injectable()
export class LotBiddingModalGuideService {
  private driver: Driver = driver();
  private active = false;
  private runId = 0;
  private activeRunId = 0;
  private params: StartLotBiddingModalGuideParamsType | null = null;

  constructor(private readonly translateService: TranslateService) {}

  get isActive(): boolean {
    return this.active;
  }

  start(params: StartLotBiddingModalGuideParamsType): void {
    if (this.active) {
      this.forceClose();
    }

    this.params = params;
    this.active = true;

    this.startAtStep('bidAmount');
  }

  handleNewGroupCreated(): void {
    if (!this.active) {
      return;
    }

    this.removeCreateGroupButtonPulse();
    this.startAtStep('groupFirst');
  }

  handleNewGroupRemoved(target: RemoveNewGroupGuideTarget = 'afterGrouping'): void {
    if (!this.active) {
      return;
    }

    this.removeDeleteGroupButtonPulse();

    if (target === 'createGroup') {
      this.startAtStep('createGroup');
      this.addCreateGroupButtonPulse();
      return;
    }

    this.startAfterGrouping();
  }

  forceClose(): void {
    this.resetDriver();
    this.cleanup();
  }

  private startAfterGrouping(): void {
    this.removeCreateGroupButtonPulse();
    this.startAtFirstAvailableStep(['calculator', 'condition', 'comment', 'notifyResult']);
  }

  private startAtFirstAvailableStep(stepNames: LotBiddingModalGuideStepName[]): void {
    const steps = this.buildAvailableSteps();
    const stepIndex = steps.findIndex((step) => stepNames.includes(step.guideStepName));

    if (stepIndex < 0) {
      return;
    }

    this.restartDriver(steps, stepIndex);
  }

  private startAtStep(stepName: LotBiddingModalGuideStepName): void {
    const steps = this.buildAvailableSteps();
    const stepIndex = steps.findIndex((step) => step.guideStepName === stepName);

    if (stepIndex < 0) {
      return;
    }

    this.restartDriver(steps, stepIndex);
  }

  private restartDriver(steps: LotBiddingModalDriveStep[], stepIndex: number): void {
    this.resetDriver();
    this.driver.setConfig(this.buildConfig(steps));
    this.driver.drive(stepIndex);
  }

  private buildConfig(steps: LotBiddingModalDriveStep[]): Config {
    const runId = this.createRunId();

    return {
      prevBtnText: this.translate('DRIVER_CONFIG.PREV_BTN'),
      nextBtnText: this.translate('DRIVER_CONFIG.NEXT_BTN'),
      doneBtnText: this.translate('DRIVER_CONFIG.DONE_BTN'),
      steps,
      onDestroyed: () => this.handleDestroyed(runId),
    };
  }

  private createRunId(): number {
    this.runId += 1;
    this.activeRunId = this.runId;

    return this.runId;
  }

  private invalidateCurrentRun(): void {
    this.runId += 1;
    this.activeRunId = this.runId;
  }

  private handleDestroyed(runId: number): void {
    if (runId !== this.activeRunId) {
      return;
    }

    this.cleanup();
  }

  private resetDriver(): void {
    this.invalidateCurrentRun();

    try {
      this.driver.destroy();
    } catch (error) {
      console.warn('Lot bidding modal guide destroy failed', error);
    }

    this.driver = driver();
  }

  private buildAvailableSteps(): LotBiddingModalDriveStep[] {
    return this.buildSteps().filter((step) => this.isStepAvailable(step));
  }

  private buildSteps(): LotBiddingModalDriveStep[] {
    return [
      this.buildBidAmountStep(),
      this.buildCreateGroupStep(),
      this.buildGroupFirstStep(),
      this.buildGroupSecondStep(),
      this.buildRemoveGroupStep(),
      this.buildCalculatorStep(),
      this.buildConditionStep(),
      this.buildCommentStep(),
      this.buildCreateTranslationFirstStep(),
      this.buildCreateTranslationSecondStep(),
      this.buildNotifyResultStep(),
      this.buildBidRulesStep(),
      this.buildGreenCornerStep(),
      this.buildFinishStep(),
    ].filter((step): step is LotBiddingModalDriveStep => Boolean(step));
  }

  private buildBidAmountStep(): LotBiddingModalDriveStep {
    return this.buildAnchorStep(
      'bidAmount',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.bidAmount,
      'LOT_BIDDING_MODAL.GUIDE.BID_AMOUNT.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.BID_AMOUNT.DESCR',
      {
        onNextClick: () => {
          if (this.params?.isCanGroupBids && !this.params.isNewGroup()) {
            this.addCreateGroupButtonPulse();
          }

          this.driver.moveNext();
        },
      },
    );
  }

  private buildCreateGroupStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.isCanGroupBids || this.params.isNewGroup()) {
      return null;
    }

    return this.buildAnchorStep(
      'createGroup',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.createGroup,
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.ZERO.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.ZERO.DESCR',
      {
        side: 'bottom',
        showButtons: ['close'],
      },
    );
  }

  private buildGroupFirstStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.isCanGroupBids || !this.params.isNewGroup()) {
      return null;
    }

    return this.buildAnchorStep(
      'groupFirst',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.groupContainer,
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.FIRST.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.FIRST.DESCR',
      {
        side: 'left',
        onPrevClick: () => {
          this.params?.removeNewGroup({
            notifyGuide: true,
            guideTarget: 'createGroup',
          });
        },
        onNextClick: () => this.driver.moveNext(),
      },
    );
  }

  private buildGroupSecondStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.isCanGroupBids || !this.params.isNewGroup()) {
      return null;
    }

    return this.buildAnchorStep(
      'groupSecond',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.groupContainer,
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.SECOND.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.SECOND.DESCR',
      {
        side: 'right',
        onNextClick: () => {
          this.addDeleteGroupButtonPulse();
          this.driver.moveNext();
        },
      },
    );
  }

  private buildRemoveGroupStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.isCanGroupBids || !this.params.isNewGroup()) {
      return null;
    }

    return this.buildAnchorStep(
      'removeGroup',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.removeGroup,
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.CLOSE.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.BIDS_GROUPING.CLOSE.DESCR',
      {
        side: 'right',
        showButtons: ['close'],
      },
    );
  }

  private buildCalculatorStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.renderCalculator || this.params.isOtherCountriesUser) {
      return null;
    }

    return this.buildAnchorStep(
      'calculator',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.calculator,
      'LOT_BIDDING_MODAL.GUIDE.CALC.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.CALC.DESCR',
      {
        side: 'left',
        onPrevClick: () => {
          if (this.params?.isCanGroupBids && !this.params.isNewGroup()) {
            this.startAtStep('createGroup');
            this.addCreateGroupButtonPulse();
            return;
          }

          this.startAtStep('bidAmount');
        },
        onNextClick: () => this.driver.moveNext(),
      },
    );
  }

  private buildConditionStep(): LotBiddingModalDriveStep {
    return this.buildAnchorStep(
      'condition',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.condition,
      'LOT_BIDDING_MODAL.GUIDE.CONDITION.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.CONDITION.DESCR',
    );
  }

  private buildCommentStep(): LotBiddingModalDriveStep {
    return this.buildAnchorStep(
      'comment',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.comment,
      'LOT_BIDDING_MODAL.GUIDE.COMMENT.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.COMMENT.DESCR',
    );
  }

  private buildCreateTranslationFirstStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.canCreateTranslation || this.params.isSanctioned) {
      return null;
    }

    return this.buildAnchorStep(
      'createTranslationFirst',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.createTranslation,
      'LOT_BIDDING_MODAL.GUIDE.LOT_TRANS.FIRST.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.LOT_TRANS.FIRST.DESCR',
      {
        side: 'left',
      },
    );
  }

  private buildCreateTranslationSecondStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.canCreateTranslation || this.params.isSanctioned) {
      return null;
    }

    return this.buildAnchorStep(
      'createTranslationSecond',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.createTranslationHint,
      'LOT_BIDDING_MODAL.GUIDE.LOT_TRANS.SECOND.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.LOT_TRANS.SECOND.DESCR',
      {
        side: 'right',
      },
    );
  }

  private buildNotifyResultStep(): LotBiddingModalDriveStep {
    return this.buildAnchorStep(
      'notifyResult',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.notifyResult,
      'LOT_BIDDING_MODAL.GUIDE.NOTIFY_RES.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.NOTIFY_RES.DESCR',
    );
  }

  private buildBidRulesStep(): LotBiddingModalDriveStep {
    return this.buildAnchorStep(
      'bidRules',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.bidRules,
      'LOT_BIDDING_MODAL.GUIDE.BIDS_RULES.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.BIDS_RULES.DESCR',
    );
  }

  private buildGreenCornerStep(): LotBiddingModalDriveStep | null {
    if (!this.params?.isRussianUser) {
      return null;
    }

    return this.buildAnchorStep(
      'greenCorner',
      LOT_BIDDING_MODAL_GUIDE_ANCHORS.greenCorner,
      'LOT_BIDDING_MODAL.GUIDE.GREEN_CORNER.TITLE',
      'LOT_BIDDING_MODAL.GUIDE.GREEN_CORNER.DESCR',
    );
  }

  private buildFinishStep(): LotBiddingModalDriveStep {
    return {
      guideStepName: 'finish',
      popover: this.buildPopover({
        titleKey: 'LOT_BIDDING_MODAL.GUIDE.FINISH.TITLE',
        descriptionKey: 'LOT_BIDDING_MODAL.GUIDE.FINISH.DESCR',
      }),
    };
  }

  private buildAnchorStep(
    guideStepName: LotBiddingModalGuideStepName,
    anchor: string,
    titleKey: string,
    descriptionKey: string,
    overrides?: Partial<Popover>,
  ): LotBiddingModalDriveStep {
    return {
      guideStepName,
      element: this.buildAnchorSelector(anchor),
      popover: this.buildPopover({
        titleKey,
        descriptionKey,
        overrides,
      }),
    };
  }

  private buildPopover(params: {
    titleKey: string;
    descriptionKey: string;
    overrides?: Partial<Popover>;
  }): Popover {
    return {
      side: 'left',
      align: 'start',
      title: this.translate(params.titleKey),
      description: this.translate(params.descriptionKey),
      popoverClass: 'lot-bid-modal',
      ...params.overrides,
    };
  }

  private isStepAvailable(step: LotBiddingModalDriveStep): boolean {
    if (!step.element) {
      return true;
    }

    if (typeof step.element !== 'string') {
      return true;
    }

    const element = document.querySelector(step.element);

    return element instanceof HTMLElement && this.isElementVisible(element);
  }

  private isElementVisible(element: HTMLElement): boolean {
    return Boolean(element.offsetParent || element.getClientRects().length);
  }

  private buildAnchorSelector(anchor: string): string {
    return `[data-guide-anchor="${anchor}"]`;
  }

  private translate(key: string): string {
    return this.translateService.instant(key);
  }

  private getCreateGroupButton(): HTMLElement | null {
    return this.getAnchorElement(LOT_BIDDING_MODAL_GUIDE_ANCHORS.createGroup);
  }

  private getRemoveGroupButton(): HTMLElement | null {
    return this.getAnchorElement(LOT_BIDDING_MODAL_GUIDE_ANCHORS.removeGroup);
  }

  private getAnchorElement(anchor: string): HTMLElement | null {
    const element = document.querySelector(this.buildAnchorSelector(anchor));

    return element instanceof HTMLElement ? element : null;
  }

  private addCreateGroupButtonPulse(): void {
    this.getCreateGroupButton()?.classList.add('pulse-create-group-btn');
  }

  private removeCreateGroupButtonPulse(): void {
    this.getCreateGroupButton()?.classList.remove('pulse-create-group-btn');
  }

  private addDeleteGroupButtonPulse(): void {
    this.getRemoveGroupButton()?.classList.add('pulse-delete-group-btn');
  }

  private removeDeleteGroupButtonPulse(): void {
    this.getRemoveGroupButton()?.classList.remove('pulse-delete-group-btn');
  }

  private cleanup(): void {
    this.removeCreateGroupButtonPulse();
    this.removeDeleteGroupButtonPulse();
    this.active = false;
    this.params = null;
  }
}
